import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const fieldBase =
  'w-full rounded-xl border bg-surface-2 text-ink placeholder:text-ink-subtle ' +
  'border-line transition-colors duration-160 ease-out-soft ' +
  'focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25 focus:outline-none ' +
  'disabled:opacity-55 disabled:cursor-not-allowed';

const fieldError =
  'border-err focus:border-err focus:ring-err/25';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, error, hint, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <label className="block space-y-1.5" htmlFor={inputId}>
      {label && <span className="text-sm font-medium text-ink">{label}</span>}
      <input
        ref={ref}
        id={inputId}
        className={cn(
          fieldBase,
          'h-11 px-3.5 text-[15px]',
          error && fieldError,
          className,
        )}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error ? (
        <span className="text-xs text-err">{error}</span>
      ) : hint ? (
        <span className="text-xs text-ink-subtle">{hint}</span>
      ) : null}
    </label>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, label, error, hint, id, rows = 4, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <label className="block space-y-1.5" htmlFor={inputId}>
      {label && <span className="text-sm font-medium text-ink">{label}</span>}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        className={cn(
          fieldBase,
          'px-3.5 py-3 text-[15px] resize-y',
          error && fieldError,
          className,
        )}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error ? (
        <span className="text-xs text-err">{error}</span>
      ) : hint ? (
        <span className="text-xs text-ink-subtle">{hint}</span>
      ) : null}
    </label>
  );
});
